import { ReactNode, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Attention, AttentionItem, AttentionMail, AttentionOurs, IssueInvestigation, IssueLesson, MODE_LABEL } from '../../api/issues';
import { LoadingBlock, Refreshing } from '../Loading/Loading';
import { age, ago, moment } from './issueFormat';

export interface IAttentionPanelProps {
    attention: Attention | null;
    loading: boolean;
    error: string | null;
    /** When the next scan round starts, for the empty state. */
    nextScanAt?: string | null;
    onOpen: (item: AttentionItem) => void;
    /** Goes to the proposals. */
    onProposals: () => void;
    /** Goes to the backlog of open issues: everything older than the age limit. */
    onBacklog: () => void;
    /** Goes to the open issues of the last weeks. */
    onRecent: () => void;
    /** Goes to the mail of one environment. */
    onMail: (mail: AttentionMail) => void;
    /** Goes to the issues of a kind of problem, in the environment given or across them. */
    onProblem: (code: string, environment?: string | null) => void;
    /** Goes to the issues of a kind of problem that wait on us, in the one environment they are in or across them. */
    onOurs: (ours: AttentionOurs) => void;
    /** Goes to every issue that waits on us, of any age. */
    onAllOurs: () => void;
    onStopInvestigation: (investigation: IssueInvestigation) => void;
    /** What goes above everything else, such as the outages at Exact. */
    before?: ReactNode;
}

/** How many kinds of problem of ours show before the rest folds away. */
const OURS_SHOWN = 8;

const ICON: Record<AttentionItem['severity'], IconProp> = {
    error: 'circle-exclamation',
    warn: 'triangle-exclamation',
    info: 'circle-info',
};

/** The button of an item: what clicking it does, as a verb. */
const ACTION: Record<string, string> = {
    'new-unknown': 'Onderzoek',
    'new-ours': 'Oppakken',
    'gave-up': 'Bekijk',
    blocked: 'Bekijk',
    poison: 'Bekijk',
    'scan-failed': 'Naar de scanner',
    'exact-budget': 'Naar de scanner',
    'requeue-held': 'Naar voorstellen',
};

/**
 * Where to start: what we have to do now, then everything that waits on us, the mails, the
 * approvals, what is being looked into and which rules could be better. What runs by itself
 * and the backlog stay folded away, as numbers, so their size does not drown what is new.
 */
export const AttentionPanel = ({
    attention,
    loading,
    error,
    nextScanAt,
    onOpen,
    onProposals,
    onBacklog,
    onRecent,
    onMail,
    onProblem,
    onOurs,
    onAllOurs,
    onStopInvestigation,
    before,
}: IAttentionPanelProps) => {
    const [copied, setCopied] = useState<string | null>(null);
    const [allOurs, setAllOurs] = useState<boolean>(false);

    if (attention === null) {
        return error ? <p className="issues__error">{error}</p> : <LoadingBlock label="Uitzoeken wat er nu moet gebeuren…" />;
    }

    const urgent = attention.items.filter((i) => i.severity !== 'info');
    const other = attention.items.filter((i) => i.severity === 'info' && i.kind !== 'mails' && i.kind !== 'to-approve');
    const mails = attention.mailsTo ?? [];
    const investigations = attention.investigations ?? [];
    const lessons = attention.lessons ?? [];
    const ours = attention.ours ?? [];
    const oursShown = allOurs ? ours : ours.slice(0, OURS_SHOWN);
    const oursTotal = ours.reduce((sum, o) => sum + o.count, 0);
    const nothing = urgent.length === 0 && ours.length === 0 && mails.length === 0 && attention.toApprove === 0;

    const copyLesson = (lesson: IssueLesson) =>
        navigator.clipboard
            ?.writeText(
                JSON.stringify(
                    {
                        request: `Verbeter de regel '${lesson.code}' met de skill c2e-research-error. ${lesson.finding} ${lesson.advice}`,
                        ...lesson,
                    },
                    null,
                    2
                )
            )
            .then(() => {
                setCopied(lesson.code);
                window.setTimeout(() => setCopied(null), 2500);
            })
            .catch(() => setCopied(null));

    return (
        <Refreshing active={loading} label="Bijwerken…">
            {error && <p className="issues__error">{error}</p>}

            <div className="attention">
                <div className="attention__main">
                    <p className="issues__muted attention__updated">Bijgewerkt om {moment(attention.at)}</p>

                    {before}

                    {nothing && (
                        <p className="attention__clear">
                            <FontAwesomeIcon icon="circle-check" /> Niets te doen.{nextScanAt ? ` Volgende scan om ${moment(nextScanAt).slice(-5)}.` : ''}
                        </p>
                    )}

                    {urgent.length > 0 && (
                        <Bucket icon="bell" title="Nu doen" count={urgent.length}>
                            {urgent.map((item, index) => (
                                <Row
                                    key={`${item.kind}/${item.customer}/${item.environment}/${item.code}/${index}`}
                                    tone={item.severity}
                                    icon={ICON[item.severity]}
                                    title={item.title}
                                    detail={item.detail}
                                    meta={[
                                        item.since ? { label: 'Sinds', value: age(item.since) } : null,
                                        item.latestAt ? { label: 'Laatst', value: ago(item.latestAt) || moment(item.latestAt) } : null,
                                    ]}
                                    action={ACTION[item.kind] ?? (item.tab === 'overzicht' ? 'Naar de scanner' : 'Bekijk')}
                                    onAction={() => onOpen(item)}
                                />
                            ))}
                        </Bucket>
                    )}

                    {ours.length > 0 && (
                        <Bucket icon="gear" title="Bij ons" count={oursTotal}>
                            {oursShown.map((o) =>
                                o.code ? (
                                    <Row
                                        key={o.code}
                                        tone="warn"
                                        icon={o.unrecognised ? 'circle-question' : 'gear'}
                                        title={o.title ?? o.code}
                                        detail={o.unrecognised ? 'Nog geen regel kent deze fout. Onderzoek hem eerst.' : null}
                                        meta={[
                                            { label: 'Open', value: o.count.toLocaleString('nl-NL') },
                                            { label: 'Bij', value: o.environments.length === 1 ? o.environments[0] : `${o.environments.length} omgevingen` },
                                            o.oldestAt ? { label: 'Sinds', value: age(o.oldestAt) } : null,
                                            o.backlog > 0 ? { label: 'Achterstand', value: o.backlog.toLocaleString('nl-NL') } : null,
                                        ]}
                                        action={o.unrecognised ? 'Onderzoek' : 'Oppakken'}
                                        onAction={() => onOurs(o)}
                                    />
                                ) : (
                                    <Row
                                        key={o.environments[0]}
                                        tone="info"
                                        icon="list-ul"
                                        title={`${o.count.toLocaleString('nl-NL')} ${o.count === 1 ? 'issue' : 'issues'} bij ${o.environments[0]}`}
                                        detail="Nog niet per soort probleem geteld: de laatste scan van deze omgeving is ouder dan die telling. Scan haar opnieuw."
                                        meta={[o.latestAt ? { label: 'Laatst', value: ago(o.latestAt) || moment(o.latestAt) } : null]}
                                        action="Bekijk"
                                        onAction={() => onOurs(o)}
                                    />
                                )
                            )}
                            {ours.length > OURS_SHOWN && (
                                <li className="attention__more">
                                    <button type="button" className="btn btn-sm btn-link" onClick={() => setAllOurs(!allOurs)}>
                                        <FontAwesomeIcon icon={allOurs ? 'angle-up' : 'angle-down'} />{' '}
                                        {allOurs ? 'Minder tonen' : `Nog ${(ours.length - OURS_SHOWN).toLocaleString('nl-NL')} tonen`}
                                    </button>
                                    <button type="button" className="btn btn-sm btn-link" onClick={onAllOurs}>
                                        Alle issues bij ons <FontAwesomeIcon icon="arrow-right" />
                                    </button>
                                </li>
                            )}
                        </Bucket>
                    )}

                    {mails.length > 0 && (
                        <Bucket icon="envelope" title="Klant mailen" count={mails.length}>
                            {mails.map((mail) => (
                                <Row
                                    key={`${mail.customer}/${mail.environment}`}
                                    tone="warn"
                                    icon="envelope"
                                    title={`${mail.customer} / ${mail.environment}`}
                                    detail={mail.points === 1 ? '1 punt dat de klant nog niet kent.' : `${mail.points} punten die de klant nog niet kent.`}
                                    action="Mail opstellen"
                                    onAction={() => onMail(mail)}
                                />
                            ))}
                        </Bucket>
                    )}

                    {attention.toApprove > 0 && (
                        <Bucket icon="rotate-left" title="Goedkeuren" count={attention.toApprove}>
                            <Row
                                tone="info"
                                icon="rotate-left"
                                title={attention.toApprove === 1 ? '1 voorstel wacht op goedkeuring' : `${attention.toApprove.toLocaleString('nl-NL')} voorstellen wachten op goedkeuring`}
                                detail="Vooral betalingen. De scanner heeft ze al gecontroleerd."
                                action="Beoordelen"
                                onAction={onProposals}
                            />
                        </Bucket>
                    )}

                    {investigations.length > 0 && (
                        <Bucket icon="magnifying-glass" title="In onderzoek" count={investigations.length}>
                            {investigations.map((investigation) => (
                                <Row
                                    key={investigation.code}
                                    tone="info"
                                    icon="magnifying-glass"
                                    title={investigation.title ?? investigation.code}
                                    detail={investigation.note}
                                    meta={[
                                        { label: 'Door', value: investigation.startedBy ?? 'onbekend' },
                                        { label: 'Sinds', value: age(investigation.startedAt) },
                                        investigation.environment ? { label: 'Bij', value: investigation.environment } : null,
                                    ]}
                                    action="Bekijk"
                                    onAction={() => onProblem(investigation.code, investigation.environment)}
                                    extra={
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onStopInvestigation(investigation)}>
                                            <FontAwesomeIcon icon="check" /> Afronden
                                        </button>
                                    }
                                />
                            ))}
                        </Bucket>
                    )}

                    {lessons.length > 0 && (
                        <Bucket icon="lightbulb" title="Regels om te verbeteren" count={lessons.length}>
                            {lessons.map((lesson) => (
                                <Row
                                    key={lesson.code}
                                    tone="info"
                                    icon="lightbulb"
                                    title={lesson.title ?? lesson.code}
                                    detail={`${lesson.finding} ${lesson.advice}`}
                                    meta={[
                                        { label: 'Open', value: lesson.open.toLocaleString('nl-NL') },
                                        { label: 'Waar', value: lesson.environments.length === 1 ? lesson.environments[0] : `${lesson.environments.length} omgevingen` },
                                    ]}
                                    action={copied === lesson.code ? 'Gekopieerd' : 'Kopieer voor de skill'}
                                    onAction={() => copyLesson(lesson)}
                                    extra={
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onProblem(lesson.code)}>
                                            Bekijk
                                        </button>
                                    }
                                />
                            ))}
                        </Bucket>
                    )}

                    {other.length > 0 && (
                        <Bucket icon="circle-info" title="Ook goed om te weten" count={other.length}>
                            {other.map((item, index) => (
                                <Row
                                    key={`${item.kind}/${item.customer}/${item.environment}/${index}`}
                                    tone="info"
                                    icon={ICON.info}
                                    title={item.title}
                                    detail={item.detail}
                                    action={ACTION[item.kind] ?? 'Bekijk'}
                                    onAction={() => onOpen(item)}
                                />
                            ))}
                        </Bucket>
                    )}
                </div>

                <aside className="attention__side" aria-label="Loopt vanzelf en achterstand">
                    <div className="attention__card">
                        <h4>
                            <FontAwesomeIcon icon="bolt" /> Loopt vanzelf
                        </h4>
                        <p className="issues__muted">Opnieuw aanbieden: {MODE_LABEL[attention.mode] ?? attention.mode}</p>
                        <Count value={attention.automatic} label="gaan vanzelf opnieuw, in porties" onClick={onProposals} />
                        <Count value={attention.approved} label="goedgekeurd, gaan mee in de volgende porties" onClick={onProposals} />
                    </div>

                    <div className="attention__card">
                        <h4>
                            <FontAwesomeIcon icon="list-ul" /> Open
                        </h4>
                        <Count value={attention.openRecent} label={`recent (eerste fout in de laatste ${attention.maxAgeDays} dagen)`} onClick={onRecent} />
                        <Count value={attention.waitingOnUs} label="bij ons, elke leeftijd" onClick={onAllOurs} />
                    </div>

                    <div className="attention__card attention__card--backlog">
                        <h4>
                            <FontAwesomeIcon icon="box-archive" /> Achterstand
                        </h4>
                        <p className="issues__muted">Eerste fout langer dan {attention.maxAgeDays} dagen geleden. Gaat niet vanzelf. Beoordeel per soort probleem.</p>
                        <Count value={attention.openBacklog} label="open issues" onClick={onBacklog} />
                        <Count value={attention.backlogProposals} label="voorstellen om opnieuw aan te bieden" onClick={onProposals} />
                    </div>
                </aside>
            </div>
        </Refreshing>
    );
};

const Bucket = ({ icon, title, count, children }: { icon: IconProp; title: string; count: number; children: ReactNode }) => (
    <section className="attention__bucket" aria-label={title}>
        <h3 className="attention__bucket-title">
            <FontAwesomeIcon icon={icon} /> {title}
            <span className="attention__bucket-count">{count.toLocaleString('nl-NL')}</span>
        </h3>
        <ul className="attention__list">{children}</ul>
    </section>
);

const Row = ({
    tone,
    icon,
    title,
    detail,
    meta,
    action,
    onAction,
    extra,
}: {
    tone: string;
    icon: IconProp;
    title: string;
    detail?: string | null;
    meta?: ({ label: string; value: string } | null)[];
    action: string;
    onAction: () => void;
    extra?: ReactNode;
}) => {
    const facts = (meta ?? []).filter((m): m is { label: string; value: string } => m !== null && !!m.value);

    return (
        <li className={`attention__item attention__item--${tone}`}>
            <FontAwesomeIcon icon={icon} className="attention__icon" />
            <div className="attention__text">
                <strong>{title}</strong>
                {detail && <span>{detail}</span>}
                {facts.length > 0 && (
                    <span className="attention__meta">
                        {facts.map((f) => (
                            <span key={f.label}>
                                <span className="attention__meta-label">{f.label}:</span> {f.value}
                            </span>
                        ))}
                    </span>
                )}
            </div>
            <div className="attention__buttons">
                {extra}
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={onAction}>
                    {action} <FontAwesomeIcon icon="arrow-right" />
                </button>
            </div>
        </li>
    );
};

const Count = ({ value, label, onClick, tone }: { value: number; label: string; onClick: () => void; tone?: string }) => (
    <button type="button" className={`attention__count${value > 0 && tone ? ` attention__count--${tone}` : ''}`} onClick={onClick} disabled={value === 0}>
        <strong>{value.toLocaleString('nl-NL')}</strong>
        <span>{label}</span>
    </button>
);
